import { API_CONSTANTS } from '../constants/api-constants';
import maiaApiInstance from './maiaApiHttpService';
import {
  Me,
  MeUser,
  UsersResponse,
  TeamMember,
  TeamMembersApiResponse,
  InviteUserRequest,
  InviteResponse,
  ChangePasswordApiResponse,
  MaiaFeatureFlag,
  MaiaFeatureFlagsResponse,
  MaiaFeatureFlagUser,
  MaiaFeatureFlagUsersResponse,
} from '../types/api';

interface ApiErrorResponse {
  message: string;
  status: number;
  timestamp: string;
}

interface AxiosError {
  response?: {
    data: ApiErrorResponse;
    status: number;
  };
  message: string;
  config?: {
    url?: string;
  };
}

const tenantHeader = (tenantId?: string): Record<string, string> =>
  tenantId ? { 'X-Tenant-ID': tenantId } : {};

const toTeamMember = (user: MeUser): TeamMember => ({
  uuid: user.id,
  authUserId: user.authUserId,
  email: user.email,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  role: user.roles[0] ?? '',
  lastLogin: user.lastLoginAt,
  ehrUsername: user.ehrUsername,
});

const fetchMemberRoster = async (
  endpoint: string,
  tenantId?: string,
): Promise<TeamMember[]> => {
  const response = await maiaApiInstance.get<UsersResponse>(endpoint, {
    headers: tenantHeader(tenantId),
  });

  return (response.data.users ?? []).map(toTeamMember);
};

const matchesSearch = (member: TeamMember, searchText: string): boolean =>
  `${member.firstName} ${member.lastName} ${member.email}`
    .toLowerCase()
    .includes(searchText.toLowerCase());

const toTeamMembersResponse = (
  members: TeamMember[],
  searchText?: string,
): TeamMembersApiResponse => {
  const filtered = searchText
    ? members.filter((member) => matchesSearch(member, searchText))
    : members;
  // /v1/users returns the whole roster in one request, so there is nothing to
  // page over — surface every (filtered) member as a single complete page.
  return {
    message: 'OK',
    status: 200,
    timestamp: '',
    data: {
      models: filtered,
      totalPages: 1,
      totalElements: filtered.length,
      isFirst: true,
      isLast: true,
    },
  };
};

export const userService = {
  // Identity bootstrap read (maia-api user-mode)
  async getMe(): Promise<Me> {
    try {
      const response = await maiaApiInstance.get<Me>(
        API_CONSTANTS.ENDPOINTS.MAIA_API.ME,
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to load user';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Update a member's per-tenant attributes (role and EHR username only — name
  // and email are global, system-owned, and not admin-editable).
  async updateUserProfile(
    userData: {
      uuid: string;
      roleName: string;
      ehrUsername?: string;
    },
    tenantId: string,
  ): Promise<{ success: boolean; message: string; status: number }> {
    try {
      const ehrUsername = userData.ehrUsername?.trim();
      await maiaApiInstance.patch(
        `${API_CONSTANTS.ENDPOINTS.MAIA_API.USERS}/${userData.uuid}`,
        {
          roles: [userData.roleName],
          ...(ehrUsername ? { ehrUsername } : {}),
        },
        {
          headers: tenantHeader(tenantId),
        },
      );

      return {
        success: true,
        message: 'User profile updated successfully',
        status: 200,
      };
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to update user profile';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Remove a member (active or invited) — maia-api's unified delete. 204 on
  // success, no body.
  async deleteUser(
    uuid: string,
    tenantId: string,
  ): Promise<{ success: boolean; message: string; status: number }> {
    try {
      await maiaApiInstance.delete(
        `${API_CONSTANTS.ENDPOINTS.MAIA_API.USERS}/${uuid}`,
        {
          headers: tenantHeader(tenantId),
        },
      );

      return {
        success: true,
        message: 'User deleted successfully',
        status: 200,
      };
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to delete user';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Full team roster (maia-api user-mode), no pagination envelope
  async getAllTeamMembers(tenantId?: string): Promise<TeamMember[]> {
    try {
      return await fetchMemberRoster(
        API_CONSTANTS.ENDPOINTS.MAIA_API.USERS,
        tenantId,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch team members';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Get team members (maia-api user-mode); whole roster in one page, search client-side
  async getTeamMembers(
    searchText?: string,
    tenantId?: string,
  ): Promise<TeamMembersApiResponse> {
    try {
      const members = await fetchMemberRoster(
        API_CONSTANTS.ENDPOINTS.MAIA_API.USERS,
        tenantId,
      );
      return toTeamMembersResponse(members, searchText);
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch team members';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Dashboard-pending members (maia-api GET /v1/invites); whole list in one
  // request, search client-side.
  async getInvitations(
    searchText?: string,
    tenantId?: string,
  ): Promise<TeamMember[]> {
    try {
      const invited = await fetchMemberRoster(
        API_CONSTANTS.ENDPOINTS.MAIA_API.INVITES,
        tenantId,
      );
      return searchText
        ? invited.filter((member) => matchesSearch(member, searchText))
        : invited;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to fetch invitations';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Invite or resend a single member (maia-api POST /v1/invites is idempotent —
  // an existing membership is a resend, not an error).
  async inviteUser(request: InviteUserRequest): Promise<InviteResponse> {
    const { tenantId, email, firstName, lastName, roles, ehrUsername } =
      request;
    try {
      const response = await maiaApiInstance.post<InviteResponse>(
        API_CONSTANTS.ENDPOINTS.MAIA_API.INVITES,
        { email, firstName, lastName, roles, ehrUsername },
        {
          headers: tenantHeader(tenantId),
        },
      );

      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to invite user';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  // Request a password-reset email (maia-api POST /v1/password-resets — public,
  // tenant-agnostic; the acknowledgement is identical whether the account exists).
  async changePassword(
    email: string,
  ): Promise<{ success: boolean; message: string; status: number }> {
    try {
      const response = await maiaApiInstance.post<ChangePasswordApiResponse>(
        API_CONSTANTS.ENDPOINTS.MAIA_API.PASSWORD_RESETS,
        { email },
      );

      return {
        success: true,
        message:
          response.data.message || 'Change password email sent successfully',
        status: 200,
      };
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to send change password email';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  async getFeatureFlags(tenantId: string): Promise<MaiaFeatureFlag[]> {
    try {
      const response = await maiaApiInstance.get<MaiaFeatureFlagsResponse>(
        API_CONSTANTS.ENDPOINTS.MAIA_FEATURE_FLAG.BASE,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        },
      );

      return response.data.flags ?? [];
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to load feature flags';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  async updateFeatureFlag(
    tenantId: string,
    flagKey: string,
    enabled: boolean,
  ): Promise<void> {
    const path = `${API_CONSTANTS.ENDPOINTS.MAIA_FEATURE_FLAG.BASE}/${encodeURIComponent(flagKey)}/tenant`;
    try {
      await maiaApiInstance.put<MaiaFeatureFlag>(
        path,
        { enabled_at: enabled ? 'now' : null },
        {
          headers: {
            'X-Tenant-ID': tenantId,
            'Content-Type': 'application/json',
          },
        },
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to update feature flag';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  async getUsersByFeatureFlag(
    tenantId: string,
    featureKey: string,
  ): Promise<MaiaFeatureFlagUser[]> {
    const path = `${API_CONSTANTS.ENDPOINTS.MAIA_FEATURE_FLAG.BASE}/${encodeURIComponent(featureKey)}/users`;
    try {
      const response = await maiaApiInstance.get<MaiaFeatureFlagUsersResponse>(
        path,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        },
      );

      return response.data.users ?? [];
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to load users for this feature';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  async assignFeatureToUser(
    tenantId: string,
    body: { userId: string; featureKey: string },
  ): Promise<void> {
    const path = `${API_CONSTANTS.ENDPOINTS.MAIA_FEATURE_FLAG.BASE}/${encodeURIComponent(body.featureKey)}/users`;
    try {
      await maiaApiInstance.post<MaiaFeatureFlagUser>(
        path,
        { userId: body.userId },
        {
          headers: {
            'X-Tenant-ID': tenantId,
            'Content-Type': 'application/json',
          },
        },
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to assign feature to user';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },

  async unassignUserFromFeatureFlag(
    tenantId: string,
    body: { userId: string; featureKey: string },
  ): Promise<void> {
    const path = `${API_CONSTANTS.ENDPOINTS.MAIA_FEATURE_FLAG.BASE}/${encodeURIComponent(body.featureKey)}/users`;
    try {
      await maiaApiInstance.delete<MaiaFeatureFlagUser>(path, {
        headers: {
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
        data: { userId: body.userId },
      });
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to unassign user from feature';

      const customError = new Error(errorMessage);
      (customError as AxiosError).response = axiosError?.response;
      (customError as AxiosError).config = axiosError?.config;

      throw customError;
    }
  },
};
